import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Cohort, Level, Profile } from '@/types'
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Users,
  AlertCircle,
  Eye,
  Calendar,
  User,
  Layers,
  Lock,
  Unlock
} from 'lucide-react'

interface CohortWithRelations extends Cohort {
  level: Level | null
  teacher: Profile | null
}

export default function CohortsList() {
  const [cohorts, setCohorts] = useState<CohortWithRelations[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; cohort: CohortWithRelations | null }>({
    open: false,
    cohort: null
  })
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    loadCohorts()
  }, [])

  const loadCohorts = async () => {
    try {
      const { data: cohortsData, error: cohortsError } = await (supabase
        .from('cohorts') as any)
        .select('*')
        .order('created_at', { ascending: false })

      if (cohortsError) throw cohortsError

      const { data: levelsData } = await (supabase
        .from('levels') as any)
        .select('*')

      const { data: profilesData } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'teacher')

      const cohortsWithRelations = ((cohortsData || []) as Cohort[]).map((cohort: Cohort) => ({
        ...cohort,
        level: ((levelsData || []) as Level[]).find((l: Level) => l.id === cohort.level_id) || null,
        teacher: ((profilesData || []) as Profile[]).find((p: Profile) => p.id === cohort.teacher_id) || null
      }))

      setCohorts(cohortsWithRelations)
    } catch (error) {
      console.error('Error loading cohorts:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteModal.cohort) return

    setIsDeleting(true)
    try {
      const { error } = await (supabase
        .from('cohorts') as any)
        .delete()
        .eq('id', deleteModal.cohort.id)

      if (error) throw error

      setCohorts(cohorts.filter(c => c.id !== deleteModal.cohort?.id))
      setDeleteModal({ open: false, cohort: null })
    } catch (error) {
      console.error('Error deleting cohort:', error)
      alert('Error al eliminar la cohorte.')
    } finally {
      setIsDeleting(false)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const filteredCohorts = cohorts.filter(cohort =>
    cohort.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cohort.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cohort.level?.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cohortes</h1>
          <p className="text-gray-600">Gestiona las cohortes de estudiantes con sus profesores y niveles</p>
        </div>
        <Link
          to="/admin/cohorts/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nueva Cohorte
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar cohortes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
        />
      </div>

      {/* Cohorts Grid */}
      {filteredCohorts.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCohorts.map((cohort) => (
            <div key={cohort.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-5">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-indigo-100">
                      <Users className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{cohort.name}</h3>
                      <p className="text-xs text-gray-500">{cohort.description}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    cohort.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {cohort.is_active ? 'Activa' : 'Inactiva'}
                  </span>
                </div>

                {/* Info */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Layers className="w-4 h-4" />
                    <span>Nivel: {cohort.level?.name || 'Sin asignar'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <User className="w-4 h-4" />
                    <span>Profesor: {cohort.teacher ? `${cohort.teacher.first_name} ${cohort.teacher.last_name}` : 'Sin asignar'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>Estudiantes: {cohort.student_ids?.length || 0} / {cohort.max_students}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>Inicio: {formatDate(cohort.start_date)}</span>
                  </div>
                  {(cohort as any).schedule_day && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>Horario: {(cohort as any).schedule_day} de {(cohort as any).schedule_start_time} a {(cohort as any).schedule_end_time}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    {cohort.unlocked_unit_index > 0 ? (
                      <Unlock className="w-4 h-4 text-green-600" />
                    ) : (
                      <Lock className="w-4 h-4 text-orange-500" />
                    )}
                    <span>Unidades desbloqueadas: {cohort.unlocked_unit_index + 1}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-3 border-t">
                  <Link
                    to={`/admin/cohorts/${cohort.id}`}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    Ver
                  </Link>
                  <Link
                    to={`/admin/cohorts/${cohort.id}/edit`}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-[#0055A4] hover:bg-[#0055A4]/5 rounded-lg transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    Editar
                  </Link>
                  <button
                    onClick={() => setDeleteModal({ open: true, cohort })}
                    className="flex items-center justify-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No se encontraron cohortes</p>
          <Link
            to="/admin/cohorts/new"
            className="text-[#0055A4] hover:underline text-sm mt-2 inline-block"
          >
            Crear primera cohorte
          </Link>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-full bg-red-100">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Eliminar Cohorte</h3>
            </div>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar la cohorte "{deleteModal.cohort?.name}"?
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal({ open: false, cohort: null })}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
